import {
  Alert,
  AlertIcon,
  Button,
  Checkbox,
  CircularProgress,
  CloseButton,
  Flex,
  Link,
  Text
} from '@stars-ecom/shared-atoms-ui'
import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import set from 'lodash/set'
import isEmpty from 'lodash/isEmpty'
import React, { useContext, useEffect, useState } from 'react'

import { ApiContext, UserContext, WebsiteContext } from '../context'

const AccountPreferences = () => {
  const [preferences, setPreferences] = useState({})
  const [alert, setAlert] = useState()
  const currentUser = useContext(UserContext)
  const websiteContext = useContext(WebsiteContext)
  const apiContext = useContext(ApiContext)
  const [loading, setLoading] = useState(false)
  const windowGlobal = typeof window !== 'undefined' && window
  const scrollToTop = () => windowGlobal.scrollTo({ top: 0, behavior: 'smooth' })

  const getData = async () => {
    try {
      setLoading(true)
      const result = await apiContext?.MarketingApi?.getMarketingPreference(currentUser?.id)
      if (result) {
        setPreferences(result)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (currentUser) {
      getData()
    }
  }, [currentUser])

  const handleChange = (field) => (value) => {
    const newPreferences = cloneDeep(preferences)
    set(newPreferences, field, value ? 1 : 0)
    setPreferences(newPreferences)
  }

  const save = async () => {
    try {
      setLoading(true)
      await apiContext?.MarketingApi?.upsertMarketingPreference(currentUser?.id, preferences)
      setAlert({
        active: true,
        status: 'success',
        message: 'Préférences sauvegardées avec succés.'
      })
    } catch (e) {
      setAlert({
        active: true,
        status: 'error',
        message: 'Erreur de sauvegarde, veuillez réessayer plus tard.'
      })
    } finally {
      setLoading(false)
      scrollToTop()
    }
  }

  const Field = ({ label, field }) => (
    <Flex direction="column" align="center" justify="flex-start" width="25%">
      <Checkbox
        isChecked={get(preferences, field, false)}
        onChange={(e) => handleChange(field)(e.target.checked)}
        backgroundColorChecked={websiteContext?.mainColor}
      />
      <Text fontSize="12px" mt="10px">
        {label}
      </Text>
    </Flex>
  )
  return (
    <Flex width="100%" position="relative" backgroundColor="#ffffff" mb="20px">
      <Flex direction="column" padding={{ base: '15px', md: '40px' }} maxWidth="100%">
        <Text
          as="h2"
          fontFamily={websiteContext?.fontFamilyNarrow}
          fontSize="26px"
          borderBottom={{ base: '1px solid #c1c1c1', md: 'none' }}
          color="#333333"
          fontWeight={700}
          mb="20px">
          Mes préférences
        </Text>
        {alert && (
          <Alert status={alert.status} mb="20px">
            <AlertIcon />
            {alert.message}
            <CloseButton
              position="absolute"
              right="8px"
              top="8px"
              onClick={() => {
                setAlert(null)
              }}
            />
          </Alert>
        )}
        <Text
          mb="40px"
          fontFamily="PT Sans, Arial, sans-serif"
          fontSize="14px"
          color="#333333"
          textAlign="justify">
          Vous pouvez choisir les communications que vous souhaitez recevoir ainsi que les modalités selon 
          lesquelles nous pouvons mesurer les performances de nos communications électroniques. 
          Vous pouvez modifier vos choix ou retirer votre consentement à tout moment.
        </Text>

        <Flex direction={{ base: 'column', md: 'row' }} width="100%" mb="30px">
          <Flex direction="column" width="350px">
            <Text
              as="h3"
              fontFamily="PT Sans, Arial, sans-serif"
              fontSize="16px"
              fontWeight={700}
              color="#333333"
              mb="10px">
              Les actus, promos, nouveautés de {websiteContext?.title}
            </Text>
            <Text as="p" fontSize="11px" textAlign="justify" color="#333333" fontFamily="PT Sans, Arial, sans-serif">
              Choisissez les communications commerciales que vous souhaitez recevoir de {websiteContext?.title}. 
              Les cases cochées valent consentement pour les canaux sélectionnés. 
              Le choix du téléphone autorise {websiteContext?.title} à vous contacter pour ses offres commerciales. 
              Les appels liés à vos commandes ou à votre contrat ne sont pas concernés. 
              Vous pouvez modifier vos préférences à tout moment.
            </Text>
          </Flex>
          <Flex direction="row" flexGrow="1" pt="10px">
            <Field label="Email" field="email" />
            <Field label="SMS" field="sms" />
            <Field label="Courrier" field="mail" />
            <Field label="Téléphone" field="phone" />
          </Flex>
        </Flex>
        <Flex direction={{ base: 'column', md: 'row' }} width="100%" mb="30px">
          <Flex direction="column" width="350px">
            <Text
              as="h3"
              fontFamily="PT Sans, Arial, sans-serif"
              fontSize="16px"
              fontWeight={700}
              color="#333333"
              mb="10px">
              Les partenaires de {websiteContext?.title}
            </Text>
            <Text as="p" fontSize="11px" textAlign="justify" color="#333333" fontFamily="PT Sans, Arial, sans-serif">
              Choisissez les communications commerciales que vous souhaitez recevoir de nos partenaires. 
              Les cases cochées valent consentement à la transmission de vos coordonnées aux partenaires concernés et à leur prospection par les canaux sélectionnés. 
              Si vous choisissez le téléphone, vous acceptez d'être contacté par téléphone par ces partenaires pour leurs offres commerciales. 
              Vous pouvez retirer votre consentement à tout moment.
            </Text>
            {!isEmpty(websiteContext?.partnersListLink) && (
              <Link to={websiteContext?.partnersListLink} target="_blank">
                <Text
                  as="span"
                  fontSize="11px"
                  fontStyle="italic"
                  fontFamily="PT Sans, Arial, sans-serif"
                  mt="15px"
                  _hover={{
                    textDecoration: 'underline'
                  }}>
                    Liste de nos partenaires
                </Text>
            </Link>)}
          </Flex>
          <Flex direction="row" flexGrow="1" pt="10px">
            <Field label="Email" field="partnerEmail" />
            <Field label="SMS" field="partnerSMS" />
            <Field label="Courrier" field="partnerMail" />
            <Field label="Téléphone" field="partnerPhone" />
          </Flex>
        </Flex>
        <Flex direction={{ base: 'column', md: 'row' }} width="100%" mb="30px">
          <Flex direction="column" width="350px">
            <Text
              as="h3"
              fontFamily="PT Sans, Arial, sans-serif"
              fontSize="16px"
              fontWeight={700}
              color="#333333"
              mb="10px">
              Les offres rien que pour vous
            </Text>
            <Text as="p" fontSize="11px" color="#333333" fontFamily="PT Sans, Arial, sans-serif">
              Bénéficiez d&acute;offres exclusives ciblées rien que pour vous et ne ratez plus votre
              cadeau pour votre anniversaire.
            </Text>
          </Flex>
          <Flex direction="row" flexGrow="1" pt="10px">
            <Field label="" field="targeting" />
            <Flex width="75%" />
          </Flex>
        </Flex>
        <Flex direction={{ base: 'column', md: 'row' }} width="100%" mb="30px">
          <Flex direction="column" width="350px">
            <Text
              as="h3"
              fontFamily="PT Sans, Arial, sans-serif"
              fontSize="16px"
              fontWeight={700}
              color="#333333"
              mb="10px">
              Tracking Newsletter
            </Text>
            <Text as="p" fontSize="11px" textAlign="justify" color="#333333" fontFamily="PT Sans, Arial, sans-serif">
              J&acute;autorise MY BOUTIQUE TELESHOP à mesurer les performances des newsletters que je reçois (ouverture des messages et clics sur les liens) afin d&acute;améliorer les contenus proposés et mesurer l&acute;efficacité des campagnes*
            </Text>
            <Text as="span" mt="10px" fontSize="8px" textAlign="justify" color="#333333" fontFamily="PT Sans, Arial, sans-serif" fontStyle="italic">
              *Vous pouvez retirer cette autorisation à tout moment. Le retrait de cette autorisation n&acute;a aucune incidence sur votre abonnement à nos newsletters.
            </Text>
          </Flex>
          <Flex direction="row" flexGrow="1" pt="10px">
            <Field label="" field="pixelNewsletter" />
            <Flex width="75%" />
          </Flex>
        </Flex>
        <Flex direction="row" justify="flex-end">
          <Button
            onClick={save}
            variant="solid"
            style={{
              borderRadius: 0,
              color: '#ffffff',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              height: '40px',
              fontSize: '19px',
              paddingLeft: '30px',
              paddingRight: '30px',
              fontWeight: 400,
              fontFamily: 'PT Sans Narrow, PT Sans,Arial,sans-serif',
              boxShadow: 'none'
            }}
            backgroundColor={websiteContext.mainColor}
            _hover={{ backgroundColor: websiteContext.darkColor }}>
            Enregistrer
          </Button>
        </Flex>
      </Flex>

      {loading && (
        <Flex
          position="absolute"
          width="100%"
          height="100%"
          justify="center"
          paddingTop="90px"
          style={{ backgroundColor: 'rgb(0, 0, 0, 0.2)' }}>
          <CircularProgress isIndeterminate color={websiteContext?.mainColor} />
        </Flex>
      )}
    </Flex>
  )
}

export default AccountPreferences
